import axios from 'axios';
import {
  demoLeagueMatches,
  getDemoMatchById,
  demoChannels,
  demoStandings,
  demoTopScorers,
  tickLiveMatches
} from './demoData';

const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance (unused in demo mode)
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'API-Version': '1.0',
    'Accept': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error.response?.data || error);
  }
);

// Helper: simulate async response
const demo = (data) => Promise.resolve(data);

// API Service class
class ApiService {
  // Health and Info
  async getHealth() {
    if (DEMO_MODE) return demo({ success: true, status: 'demo' });
    return api.get('/health');
  }

  async getInfo() {
    if (DEMO_MODE) return demo({ success: true, mode: 'demo' });
    return api.get('/info');
  }

  // Countries
  async getCountries() {
    if (DEMO_MODE) return demo({ success: true, countries: [] });
    return api.get('/countries');
  }

  async getCountryById(id) {
    if (DEMO_MODE) return demo({ success: true, country: null });
    return api.get(`/countries/${id}`);
  }

  // Leagues
  async getLeagues(params = {}) {
    if (DEMO_MODE) return demo({ success: true, leagues: demoLeagueMatches.leagues.map(l => ({ name: l.name, logo: l.logo, flag: l.flag, country: l.country })) });
    return api.get('/leagues', { params });
  }

  async getTopLeagues(limit = 10) {
    if (DEMO_MODE) return demo({ success: true, leagues: demoLeagueMatches.leagues.slice(0, limit).map(l => ({ name: l.name, logo: l.logo, flag: l.flag, country: l.country })) });
    return api.get('/leagues/top', { params: { limit } });
  }

  async getLeagueById(id) {
    if (DEMO_MODE) return demo({ success: true, league: null });
    return api.get(`/leagues/${id}`);
  }

  async getLeaguesByCountry(country) {
    if (DEMO_MODE) return demo({ success: true, leagues: [] });
    return api.get(`/leagues/country/${country}`);
  }

  // Teams
  async getTeams(params = {}) {
    if (DEMO_MODE) return demo({ success: true, teams: [] });
    return api.get('/teams', { params });
  }

  async getTeamById(id) {
    if (DEMO_MODE) return demo({ success: true, team: null });
    return api.get(`/teams/${id}`);
  }

  async getTeamsByLeague(leagueId) {
    if (DEMO_MODE) return demo({ success: true, teams: [] });
    return api.get(`/teams/league/${leagueId}`);
  }

  async getTeamsByCountry(country) {
    if (DEMO_MODE) return demo({ success: true, teams: [] });
    return api.get(`/teams/country/${country}`);
  }

  async searchTeams(query, limit = 10) {
    if (DEMO_MODE) return demo({ success: true, teams: [] });
    return api.get('/teams/search', { params: { q: query, limit } });
  }

  // Matches
  async getLiveMatches() {
    if (DEMO_MODE) {
      const live = demoLeagueMatches.leagues.flatMap(l => l.matches.filter(m => m.status === 'live'));
      return demo({ success: true, matches: live });
    }
    return api.get('/matches/live');
  }

  async getTodayMatches(params = {}) {
    if (DEMO_MODE) {
      const all = demoLeagueMatches.leagues.flatMap(l => l.matches);
      return demo({ success: true, matches: all });
    }
    return api.get('/matches', { params });
  }

  async getAllMatches(params = {}) {
    if (DEMO_MODE) {
      const all = demoLeagueMatches.leagues.flatMap(l => l.matches);
      return demo({ success: true, matches: all });
    }
    return api.get('/matches', { params });
  }

  // New league-based matches API
  async getLeagueMatches(params = {}) {
    if (DEMO_MODE) {
      tickLiveMatches();
      return demo(demoLeagueMatches);
    }
    return api.get('/leagues/matches', { params });
  }

  // Standings
  async getStandings(leagueId) {
    if (DEMO_MODE) return demo({ success: true, ...(demoStandings[leagueId] || {}) });
    return api.get(`/standings/${leagueId}`);
  }

  async getAllStandings() {
    if (DEMO_MODE) return demo({ success: true, standings: demoStandings });
    return api.get('/standings');
  }

  // Top scorers
  async getTopScorers(leagueId) {
    if (DEMO_MODE) return demo({ success: true, scorers: demoTopScorers[leagueId] || [] });
    return api.get(`/scorers/${leagueId}`);
  }

  async getAllTopScorers() {
    if (DEMO_MODE) return demo({ success: true, scorers: demoTopScorers });
    return api.get('/scorers');
  }

  async getTomorrowMatches() {
    if (DEMO_MODE) return demo({ success: true, matches: [] });
    return api.get('/matches/tomorrow');
  }

  async getMatchById(id) {
    if (DEMO_MODE) {
      const match = getDemoMatchById(id);
      return demo({ success: true, match });
    }
    return api.get(`/matches/${id}`);
  }

  // Get individual match details (using the new singular route)
  async getMatchDetails(id) {
    if (DEMO_MODE) {
      tickLiveMatches();
      const match = getDemoMatchById(id);
      return demo({ success: !!match, match });
    }
    return api.get(`/match/${id}`);
  }

  async getMatchesByLeague(leagueId, params = {}) {
    if (DEMO_MODE) return demo({ success: true, matches: [] });
    return api.get(`/matches/league/${leagueId}`, { params });
  }

  async getMatchesByTeam(teamId, params = {}) {
    if (DEMO_MODE) return demo({ success: true, matches: [] });
    return api.get(`/matches/team/${teamId}`, { params });
  }

  async getMatchesByDateRange(startDate, endDate, params = {}) {
    if (DEMO_MODE) return demo({ success: true, matches: [] });
    return api.get('/matches', {
      params: {
        start_date: startDate,
        end_date: endDate,
        ...params
      }
    });
  }

  async searchMatches(query, limit = 10) {
    if (DEMO_MODE) {
      const all = demoLeagueMatches.leagues.flatMap(l => l.matches);
      const results = all.filter(m =>
        m.teams.home.name.toLowerCase().includes(query.toLowerCase()) ||
        m.teams.away.name.toLowerCase().includes(query.toLowerCase())
      ).slice(0, limit);
      return demo({ success: true, matches: results });
    }
    return api.get('/matches/search', { params: { q: query, limit } });
  }

  async getMatchStatistics(id) {
    if (DEMO_MODE) return demo({ success: true, statistics: [] });
    return api.get(`/matches/${id}/statistics`);
  }

  // Admin functions
  async getSchedulerStatus() {
    if (DEMO_MODE) return demo({ success: true, status: 'demo' });
    return api.get('/admin/scheduler/status');
  }

  async triggerLiveUpdate() {
    if (DEMO_MODE) return demo({ success: true });
    return api.post('/admin/scheduler/trigger-live');
  }

  async triggerFixtureUpdate() {
    if (DEMO_MODE) return demo({ success: true });
    return api.post('/admin/scheduler/trigger-fixtures');
  }

  async getWebSocketStats() {
    if (DEMO_MODE) return demo({ success: true, stats: {} });
    return api.get('/admin/websocket/stats');
  }

  // Anonymous Streaming (No login required)
  async getAnonymousStreamUrl(channelId) {
    if (DEMO_MODE) return demo({ success: true, url: '#demo' });
    return api.get(`/anonymous/generate/${channelId}`);
  }

  async getPublicChannels() {
    if (DEMO_MODE) return demo(demoChannels);
    return api.get('/anonymous/public/channels');
  }

  // Channels (Today's Match Channels)
  async getChannelsPageData() {
    if (DEMO_MODE) return demo(demoChannels);
    return api.get('/channels');
  }

  // Search for a specific channel
  async searchChannel(channelName) {
    if (DEMO_MODE) {
      const found = demoChannels.channels.filter(c => c.name.toLowerCase().includes(channelName.toLowerCase()));
      return demo({ success: true, channels: found });
    }
    return api.get(`/search/${encodeURIComponent(channelName)}`);
  }

  // Stream a channel
  async getChannelStream(channelName, metadata = false) {
    if (DEMO_MODE) return demo({ success: true, streamUrl: '#demo', name: channelName });
    const params = metadata ? '?metadata=true' : '';
    return api.get(`/channel/${encodeURIComponent(channelName)}${params}`);
  }

  // Get all unified provider channels
  async getAllUnifiedChannels(limit = 1000) {
    if (DEMO_MODE) return demo(demoChannels);
    return api.get('/providers/channels/all', { params: { limit } });
  }

  // HLS Proxy methods for CORS-enabled streaming
  async getProxiedStreamUrl(streamUrl) {
    if (DEMO_MODE) return '#demo';
    const encodedUrl = encodeURIComponent(streamUrl);
    return `${API_BASE_URL}/hls/proxy/${encodedUrl}`;
  }

  async getChannelStreamProxied(channelName) {
    if (DEMO_MODE) return { success: true, streamUrl: '#demo', proxiedUrl: '#demo', originalUrl: '#demo', name: channelName };
    const streamData = await this.getChannelStream(channelName);
    if (streamData && streamData.streamUrl) {
      return {
        ...streamData,
        proxiedUrl: await this.getProxiedStreamUrl(streamData.streamUrl),
        originalUrl: streamData.streamUrl
      };
    }
    return streamData;
  }

  async getHlsProxyHealth() {
    if (DEMO_MODE) return demo({ success: true, status: 'demo' });
    return api.get('/hls/health');
  }
}

// Create and export instance
const apiService = new ApiService();

export default apiService;

// Export individual methods for convenience
export const {
  getHealth,
  getInfo,
  getCountries,
  getLeagues,
  getTopLeagues,
  getTeams,
  getLiveMatches,
  getTodayMatches,
  getTomorrowMatches,
  searchMatches,
  searchTeams
} = apiService;
